// @ts-nocheck
// Operational Audit Trail — Fase 15.7
export type AuditCategory = 'user_action'|'tv_control'|'executive_action'|'system_mutation'|'critical_operation'|'auth'
export type AuditLevel    = 'info' | 'warning' | 'critical'

export interface AuditEntry {
  id: string; timestamp: string; category: AuditCategory; level: AuditLevel
  actor: string; action: string; target: string | null; companyId: string | null
  obraId: string | null; metadata: Record<string, unknown>; sessionId: string | null
}
export interface AuditQuery {
  category?: AuditCategory; level?: AuditLevel; actor?: string; companyId?: string
  obraId?: string; from?: string; to?: string; limit?: number
}
export interface AuditSummary {
  timestamp: string; totalEntries: number; criticalCount: number; last24hCount: number
  byCategory: Record<AuditCategory, number>; recentCritical: AuditEntry[]
}

const MAX = 5_000
let _entries: AuditEntry[] = []
let _session: string | null = null
function gid(): string { return `aud_${Date.now()}_${Math.random().toString(36).slice(2,9)}` }

export function setAuditSession(id: string): void { _session = id }

export function audit(p: { category: AuditCategory; level?: AuditLevel; actor: string; action: string; target?: string; companyId?: string; obraId?: string; metadata?: Record<string, unknown> }): AuditEntry {
  const e: AuditEntry = { id: gid(), timestamp: new Date().toISOString(), category: p.category,
    level: p.level ?? 'info', actor: p.actor, action: p.action, target: p.target ?? null,
    companyId: p.companyId ?? null, obraId: p.obraId ?? null, metadata: p.metadata ?? {}, sessionId: _session }
  _entries.push(e)
  if (_entries.length > MAX) _entries = _entries.slice(-MAX)
  return e
}

export function auditUserAction(actor: string, action: string, opts: { target?: string; companyId?: string; obraId?: string; metadata?: Record<string, unknown> } = {}): AuditEntry {
  return audit({ category: 'user_action', actor, action, ...opts })
}
export function auditTVControl(actor: string, action: string, obraId: string, metadata?: Record<string, unknown>): AuditEntry {
  return audit({ category: 'tv_control', level: 'warning', actor, action, obraId, metadata })
}
export function auditExecutiveAction(actor: string, action: string, companyId: string, metadata?: Record<string, unknown>): AuditEntry {
  return audit({ category: 'executive_action', actor, action, companyId, metadata })
}
export function auditSystemMutation(action: string, target: string, metadata?: Record<string, unknown>): AuditEntry {
  return audit({ category: 'system_mutation', level: 'warning', actor: 'system', action, target, metadata })
}
export function auditCriticalOperation(actor: string, action: string, opts: { target?: string; companyId?: string; obraId?: string; metadata?: Record<string, unknown> } = {}): AuditEntry {
  return audit({ category: 'critical_operation', level: 'critical', actor, action, ...opts })
}

export function queryAuditLog(q: AuditQuery = {}): AuditEntry[] {
  let r = [..._entries]
  if (q.category)  r = r.filter(e => e.category  === q.category)
  if (q.level)     r = r.filter(e => e.level      === q.level)
  if (q.actor)     r = r.filter(e => e.actor      === q.actor)
  if (q.companyId) r = r.filter(e => e.companyId  === q.companyId)
  if (q.obraId)    r = r.filter(e => e.obraId     === q.obraId)
  if (q.from)      r = r.filter(e => e.timestamp  >= q.from!)
  if (q.to)        r = r.filter(e => e.timestamp  <= q.to!)
  return r.slice(-(q.limit ?? 200))
}

export function buildAuditSummary(): AuditSummary {
  const now  = Date.now(); const day = 24 * 3600_000
  const cats: AuditCategory[] = ['user_action','tv_control','executive_action','system_mutation','critical_operation','auth']
  const byCategory = Object.fromEntries(cats.map(c => [c, _entries.filter(e => e.category === c).length])) as Record<AuditCategory, number>
  return {
    timestamp:      new Date().toISOString(),
    totalEntries:   _entries.length,
    criticalCount:  _entries.filter(e => e.level === 'critical').length,
    last24hCount:   _entries.filter(e => now - new Date(e.timestamp).getTime() < day).length,
    byCategory,
    recentCritical: _entries.filter(e => e.level === 'critical').slice(-5),
  }
}
export function getAuditEntries(): AuditEntry[] { return [..._entries] }
export function resetAuditTrail(): void         { _entries = []; _session = null }
